/**
 * Shared helper to build consistent labels for assets linked to transactions.
 * 
 * Requirement:
 * - Property: "Street Address - AssetSubType"
 * - Vehicle: "Year Make Model"
 */
export const getDomainForCategory = (cat) => {
  const c = String(cat || '').toLowerCase().replace(/[_\s–—-]+/g, ' ');
  if (c.includes('taxes') || c.includes('home tax payment')) return 'taxes';
  if (c.includes('internet') || c.includes('cable') || c.includes('tv') || c.includes('streaming')) return 'media';
  if (c.includes('utilities') || c.includes('water') || c.includes('electric') || c.includes('gas') || c.includes('trash') || c.includes('waste') || c.includes('sewer')) return 'utilities';
  if (c.includes('phone') && !c.includes('cellphone') && !c.includes('mobile')) return 'utilities';
  if (c.includes('home') || c.includes('repair') || c.includes('maintenance') || c.includes('debt payment')) return 'home';
  if (c.includes('landscaping') || c.includes('garden') || c.includes('lawn') || c.includes('yard waste')) return 'landscaping';
  if (c.includes('trash') || c.includes('waste') || c.includes('sanitation') || c.includes('recycling')) return 'utilities'; 
  if (c.includes('mowing') || c.includes('snow') || c.includes('leaf') || c.includes('landscape')) return 'landscaping';
  if (c.includes('automotive') || c.includes('vehicle') || c.includes('gas fuel') || c.includes('licensing') || c.includes('tags')) return 'vehicle';
  return 'generic';
};

export const buildAssetLabel = (asset, assetMaps = {}) => {
  if (!asset) return '🔗 Asset';

  // If the backend has already provided a unified label, prefer it.
  // We check for several formats that indicate a real label vs a generic fallback
  if (asset.label) {
    const l = String(asset.label);
    const isGeneric = l.startsWith('Asset ') || l.startsWith('Structure ') || l.startsWith('Vehicle ') || l.startsWith('Physical Asset ') || l.startsWith('Land ');
    // If it contains a " - " it's likely our "Address - Type" format, so keep it even if it starts with one of those
    if (!isGeneric || l.includes(' - ')) {
      return l;
    }
  }

  try {
    const type2 = (asset.asset_type_2 || asset.type_2 || asset.type2 || '').toLowerCase();
    const isLandscaping = assetMaps.transactionDomain === 'landscaping' || assetMaps.transactionDomain === 'waste';

    // 1. Vehicle: "Year Make Model"
    if (type2 === 'vehicle') {
      const parts = [];
      if (asset.year) parts.push(asset.year);
      if (asset.make) parts.push(asset.make);
      if (asset.model) parts.push(asset.model);
      
      if (parts.length > 0) return parts.join(' ');
      return asset.label || asset.description || 'Vehicle';
    }

    // 2. Residential Property: "Street Address - AssetSubType"
    if (type2 === 'resi_property') {
      const street = asset.address?.street_address || asset.label || '';
      if (isLandscaping) {
        return `${street}${street ? ' - ' : ''}Land`;
      }
      const rawSubType = asset.asset_type_1 || asset.type_1 || asset.type1 || 'Property';
      if (rawSubType === 'resi_property' || rawSubType === 'physical_asset' || rawSubType === 'Property') {
        return street || 'Property';
      }
      const subType = rawSubType.replace(/ORM$/, '');
      return `${street}${street ? ' - ' : ''}${subType}`;
    }

    // 3. Structure or Land (linked to a Property)
    if (type2 === 'structure' || type2 === 'land') {
      const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent || null;
      const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
      
      let street = '';
      if (rpId != null && assetMaps.resiAddressById) {
        street = assetMaps.resiAddressById.get(String(rpId)) || '';
      }
      if (!street && rp && typeof rp === 'object' && rp.address) {
        street = rp.address.street_address || '';
      }
      if (!street && asset.property_label) {
        // Some serializers might provide a property_label already formatted
        street = asset.property_label;
      }

      const subTypeBase = asset.asset_type_1 || asset.type_1 || asset.type1 || (type2 === 'land' ? 'Land' : 'Structure');
      
      let suffix = '';
      if (type2 === 'structure') {
        if (isLandscaping) {
          suffix = ' - Land';
        } else {
          const ordinal = assetMaps.structOrdinalById ? assetMaps.structOrdinalById.get(String(asset.id)) : '';
          const subType = (subTypeBase === 'Structure' || subTypeBase === 'physical_asset' || subTypeBase === 'structure') ? 'Structure' : subTypeBase.replace(/ORM$/, '');
          const ordinalPart = (ordinal && String(ordinal) !== '1') ? ' ' + ordinal : '';
          suffix = ` - ${subType}${ordinalPart}`;
        }
      } else if (type2 === 'land') {
        suffix = ' - Land';
      }

      const label = `${street ? street : ''}${suffix}`;
      return label.startsWith(' - ') ? label.slice(3) : label;
    }

    // Fallback
    if (isLandscaping) {
      let label = asset.label || asset.description || 'Asset';
      if (!label.toLowerCase().includes(' - land') && !label.toLowerCase().endsWith(' land')) {
        label = `${label} - Land`;
      }
      return label;
    }
    return asset.label || asset.description || '🔗 Asset';
  } catch (e) {
    console.error("Error building asset label", e);
    return '🔗 Asset';
  }
};
